import { useRef, useState, useCallback } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crop as CropIcon, Check, X } from "lucide-react";

// Build a centered crop. If aspect is given, keep that ratio; otherwise 90% box.
function buildInitialCrop(width, height, aspect) {
  if (aspect) {
    return centerCrop(
      makeAspectCrop({ unit: "%", width: 80 }, aspect, width, height),
      width,
      height
    );
  }
  return centerCrop({ unit: "%", width: 90, height: 90, x: 5, y: 5 }, width, height);
}

function toCroppedDataUrl(image, pixelCrop) {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const cw = Math.max(1, Math.round(pixelCrop.width * scaleX));
  const ch = Math.max(1, Math.round(pixelCrop.height * scaleY));
  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    cw,
    ch
  );
  return canvas.toDataURL("image/png");
}

export function CropDialog({ open, src, onClose, onApply, theme = "dark" }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [aspect, setAspect] = useState(undefined); // undefined = free-form
  const isDark = theme === "dark";

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(buildInitialCrop(width, height, aspect));
    setCompleted(null);
  }, [aspect]);

  const changeAspect = (a) => {
    setAspect(a);
    const img = imgRef.current;
    if (img) {
      const c = buildInitialCrop(img.width, img.height, a);
      setCrop(c);
      // convert percent crop to pixel for completed
      setCompleted({
        unit: "px",
        x: (c.x / 100) * img.width,
        y: (c.y / 100) * img.height,
        width: (c.width / 100) * img.width,
        height: (c.height / 100) * img.height,
      });
    }
  };

  const apply = () => {
    const img = imgRef.current;
    if (!img || !completed || !completed.width || !completed.height) {
      onClose?.();
      return;
    }
    const url = toCroppedDataUrl(img, completed);
    onApply?.(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose?.() : null)}>
      <DialogContent className={`max-w-lg ${isDark ? "border-[#29466f] bg-[#07132e] text-slate-100 [&>button]:text-slate-300 [&>button:hover]:bg-[#152541]" : "border-[#bfdbfe] bg-[#eff6ff] text-[#17345d] [&>button]:text-[#17345d] [&>button:hover]:bg-[#dbeafe]"}`} data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CropIcon className="h-4 w-4" /> Crop gambar
          </DialogTitle>
          <DialogDescription className={isDark ? "text-slate-400" : "text-[#64748b]"}>
            Geser &amp; ubah ukuran kotak seleksi pada gambar, lalu terapkan. Hanya gambar ini yang diubah.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold tracking-wide ${isDark ? "text-slate-400" : "text-[#64748b]"}`}>RASIO</span>
          <Button size="sm" variant={aspect === undefined ? "default" : "outline"}
            className={`h-8 rounded-lg ${aspect === undefined ? "border-blue-500 bg-blue-600 text-white hover:bg-blue-700" : isDark ? "border-[#29466f] bg-[#0b1933] text-slate-200 hover:bg-[#152541]" : "border-[#bfdbfe] bg-white text-[#17345d] hover:bg-[#dbeafe]"}`} data-testid="crop-aspect-free"
            onClick={() => changeAspect(undefined)}>Bebas</Button>
          <Button size="sm" variant={aspect === 1 ? "default" : "outline"}
            className={`h-8 rounded-lg ${aspect === 1 ? "border-blue-500 bg-blue-600 text-white hover:bg-blue-700" : isDark ? "border-[#29466f] bg-[#0b1933] text-slate-200 hover:bg-[#152541]" : "border-[#bfdbfe] bg-white text-[#17345d] hover:bg-[#dbeafe]"}`} data-testid="crop-aspect-square"
            onClick={() => changeAspect(1)}>1:1</Button>
        </div>

        <div className={`flex max-h-[52vh] items-center justify-center overflow-auto rounded-xl border p-2 ${isDark ? "border-[#29466f] bg-[#0b1933]" : "border-[#bfdbfe] bg-[#dbeafe]"}`}>
          {src ? (
            <ReactCrop
              crop={crop}
              onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
              onComplete={(pixelCrop) => setCompleted(pixelCrop)}
              aspect={aspect}
              keepSelection
            >
              <img
                ref={imgRef}
                src={src}
                alt="Crop"
                onLoad={onImageLoad}
                data-testid="crop-image"
                style={{ maxHeight: "48vh", maxWidth: "100%" }}
              />
            </ReactCrop>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" className={isDark ? "text-slate-200 hover:bg-[#152541]" : "text-[#17345d] hover:bg-[#dbeafe]"} onClick={onClose} data-testid="crop-cancel-button">
            <X className="mr-2 h-4 w-4" /> Batal
          </Button>
          <Button className="bg-[#3475e8] text-white hover:bg-[#2563eb]" onClick={apply} data-testid="crop-apply-button">
            <Check className="mr-2 h-4 w-4" /> Terapkan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
